import { sortedByDate } from './arrays'
import { getAllElementAudits, getElementAudit, getPreviousMap, setPreviousMap } from './audit'
import { auth } from './auth'
import { dateFromInput, sameDay, utcDateString } from './date-time'
import { diagnosisLinkString } from './diagnosis'
import {
  Audit,
  ChangeReason,
  Diagnosis,
  Disagreement,
  DownCode,
  LostRevenue,
  Person,
  Procedure,
  ReturnItem,
  TrackingCode,
  Worksheet,
  WorksheetBase,
  WorksheetComponents,
  getAuditInfo,
  getRebuttalDisagreemens,
} from './domain'
import { log } from './log'
import { modifierLinkString } from './procedure'

export function normalizeServiceDates(w: Worksheet): Worksheet {
  // serviceDate is a calendar date, not an instant. It must leave here as a date-only
  // string ("yyyy-MM-dd") — never a Date/ISO timestamp, because JSON.stringify(Date)
  // emits a timezone-bearing value (e.g. "2026-07-14T18:30:00.000Z") that shifts the
  // calendar day across timezones. Clone procedures/diagnoses so we return a normalized
  // copy for the payload without mutating the live store.
  return {
    ...w,
    procedures: (w.procedures || []).map((v) => ({
      ...v,
      audit: undefined,
      serviceDate: utcDateString(v.serviceDate) as any,
    })),
    diagnoses: (w.diagnoses || []).map((v) => ({
      ...v,
      audit: undefined,
    })),
  }
}

export type WorksheetElement = Diagnosis | Procedure | DownCode | LostRevenue | TrackingCode | ReturnItem | Audit

export function elementCode(el: WorksheetElement): string {
  if (!el) {
    return ''
  }
  const c: any = el
  return c.diagnosisCode || c.cptCode || c.actualCPTCode || c.code || ''
}

export function createdByCurrentUser(el: WorksheetBase | WorksheetElement): boolean {
  // if (!el?.createdBy) {
  //   return true
  // }

  const signedId = auth.signedIn
  if (!signedId) {
    return true
  }

 // const { userId, email } = el.createdBy

  // if (signedId.email === email || signedId.userId === userId) {
  //   return true
  // }

  return false
}

export function hasValidMessage(item: WorksheetBase): boolean {
  if (!item?.messagesInformation) {
    return false
  }
  return item.messagesInformation.length > 0
}

export function isResolved(item: WorksheetBase): boolean {
  return item?.isResolved
}

type LastEdits = {
  lastOwner?: Person
  sortedIcds?: Diagnosis[]
  lastIcd?: Diagnosis
  sortedCpts?: Procedure[]
  lastCpt?: Procedure
}

export function lastEdits(w: Worksheet): LastEdits {
  const { procedures, diagnoses, workflowOwners } = w || {}
  const out: LastEdits = {}
  out.lastOwner = workflowOwners && workflowOwners[workflowOwners.length - 1]
  out.sortedIcds = sortedByDate(diagnoses, (a) => dateFromInput(a.createDate))
  out.sortedCpts = sortedByDate(procedures, (a) => dateFromInput(a.createDate))
  out.lastIcd = out.sortedIcds[out.sortedIcds.length - 1]
  out.lastCpt = out.sortedCpts[out.sortedCpts.length - 1]
  return out
}

type CompareApi = LastEdits & {
  createdBy(e: WorksheetElement, checkSession?: boolean): boolean
}

export function compareApi(w: Worksheet): CompareApi {
  const edits = lastEdits(w)
  const today = new Date()
  const islastOwner = !edits.lastOwner || (edits.lastOwner && edits.lastOwner.userId === auth?.signedIn?.principalId)

  function createdBy(e: WorksheetElement, checkSession = false): boolean {
    let checked = true
    if (checkSession && e.createDate) {
      const d = dateFromInput(e.createDate)
      checked = sameDay(d, today)
      console.log('checkSession', checked)
    }
    return islastOwner && checked && createdByCurrentUser(e)
  }
  return { ...edits, createdBy }
}

function setNote(c: Procedure | Diagnosis): void {
  if (c.stickyNote) {
    c.note = { note: c.stickyNote }
  }
}

export function setDisagreements(
  audit: Audit,
  allDisagreements: ChangeReason[],
  workflow: string,
  id?: string,
  it?: number,
): void {
  let disagreements: Disagreement[] = []
  
  if (!allDisagreements?.length && it === 1) {
    disagreements.push({ workflow })
  } else {
    if (allDisagreements.length) {
      const existing = allDisagreements.find((v) => v.lineId === id && v.disagreementReasons?.length > 0)
    
      if (existing) {

        disagreements = existing.disagreementReasons || []

        if(it <= disagreements.length) {
          console.info('INCORRECT rebuttal iteration, setting temp iteration to avoid overwriting', it);
          log.error('INCORRECT rebuttal iteration, setting temp iteration to avoid overwriting', workflow);
        }

        // Re enable this if we are overwriting disagreements
        // it = Math.max(disagreements.length + 1, it)

        if (disagreements.length === it - 1 && it <= 4) {
          
          disagreements.push({ workflow })

        } else if (it === 4) {

          let last = disagreements[disagreements.length - 1]
          for (let i = 0; i < 3; i++) {
            const d = disagreements[i]
            if (!d) {
              disagreements[i] = { ...last }
            }
            last = disagreements[i]
          }
          disagreements.push({ workflow })
        }
      }
    }
  }
  if (disagreements.length > 1) {
    disagreements = disagreements.slice(0, it || 1)
  } else {
    for (let i = 0; i < it; i++) {
      const d = disagreements[i]
      if (!d) {
        disagreements[i] = { workflow }
      }
    }
  }

  audit.disagreementReasons = disagreements
}
export function setProcedureTransients(
  p: Procedure,
  diagnoses: Diagnosis[],
  workflow: string,
  allDisagreements?: ChangeReason[],
  iteration?: number,
): Procedure {
  setNote(p)
  p.serviceDate = dateFromInput(p.serviceDate)
  p.links = diagnosisLinkString(diagnoses, p.diagnosisList)
  p.modifier = modifierLinkString(p)
  p.secondaryProvider ||= null
  p.tertiaryProvider ||= null
  p.audit = getElementAudit(p, workflow)
  p.audit.sourceObject = 'Procedure'
  p.audit.lineId = p.procedureId
  if (iteration) {
    setDisagreements(p.audit, allDisagreements, workflow, p.procedureId, iteration)
  }
  return p
}

export function setDiagnosisTransients(
  d: Diagnosis,
  workflow: string,
  allDisagreements?: ChangeReason[],
  iteration?: number,
): Diagnosis {
  d.audit = getElementAudit(d, workflow)
  d.audit.sourceObject = 'Diagnosis'
  d.audit.lineId = d.diagnosisId

  if (iteration) {
    setDisagreements(d.audit, allDisagreements, workflow, d.diagnosisId, iteration)
  }
  setNote(d)
  return d
}

export function setWorksheetTransients(
  sheets: WorksheetComponents[],
  audit: Record<string, unknown>,
  workflow: string,
) {
  let hasAuditSummary = false

  sheets.forEach((c) => {
    const w = c.worksheet
    const info = getAuditInfo(w, workflow)
    const group = info?.auditGroup
    const allDisagreements: ChangeReason[] = group ? getRebuttalDisagreemens(w, group) : []
    const iteration = w.rebuttalIteration || 1

    if (w.procedures) {
      w.procedures.forEach((p: Procedure) => {
        setProcedureTransients(p, w.diagnoses || [], workflow, allDisagreements, iteration)
        setPreviousMap(p, p.audit)

        audit.procedures = audit.procedures || p.audit?.changeReasons?.length
        audit.hasAuditProcedures = getAllElementAudits(p).length > 0
        hasAuditSummary ||= !!audit.hasAuditProcedures
      })
    }

    if (w.diagnoses) {
      w.diagnoses.forEach((d) => {
        setDiagnosisTransients(d, workflow, allDisagreements, iteration)
        setPreviousMap(d, d.audit)

        audit.diagnoses = audit.diagnoses || d.audit?.changeReasons?.length
        audit.hasAuditDiagnoses = getAllElementAudits(d).length > 0
        hasAuditSummary ||= !!audit.hasAuditDiagnoses
      })
    }

    if (w.reasonForVisit) {
      w.diagnoses.forEach((d) => {
        d.audit = getElementAudit(d, workflow)
        setPreviousMap(d, d.audit)

        audit.reasonForVisit = audit.reasonForVisit || d.audit?.changeReasons?.length
        setNote(d)
        audit.hasAuditReasonForVisit = getAllElementAudits(d).length > 0
        hasAuditSummary ||= !!audit.hasAuditReasonForVisit
      })
    }

    if (c.downCodes) {
      c.downCodes.forEach((v) => {
        v.audit = getElementAudit(v, workflow)
        v.audit.sourceObject = 'DownCode'
        v.audit.lineId = v.downcodeId
        setPreviousMap(v, v.audit)

        setDisagreements(v.audit, allDisagreements, workflow, v.downcodeId, iteration)

        audit.downCodes = audit.downCodes || v.audit?.changeReasons?.length
        audit.hasAuditDownCodes = getAllElementAudits(v).length > 0
        hasAuditSummary ||= !!audit.hasAuditDownCodes
      })
    }

    if (c.ncnbReasons) {
      c.ncnbReasons.forEach((v) => {
        v.audit = getElementAudit(v, workflow)
        v.audit.sourceObject = 'NCNBReason'
        v.audit.lineId = v.ncnbReasonId
        setPreviousMap(v, v.audit)

        setDisagreements(v.audit, allDisagreements, workflow, v.ncnbReasonId, iteration)

        audit.hasAuditNCNB = getAllElementAudits(v).length > 0
        hasAuditSummary ||= !!audit.hasAuditNCNB
      })
    }

    if (c.lostRevenues) {
      c.lostRevenues.forEach((v) => {
        v.audit = getElementAudit(v, workflow)
        v.audit.sourceObject = 'LostRevenue'
        v.audit.lineId = v.lostRevenueId
        setPreviousMap(v, v.audit)

        setDisagreements(v.audit, allDisagreements, workflow, v.lostRevenueId, iteration)

        audit.lostRevenues = audit.lostRevenues || v.audit?.changeReasons?.length
        audit.hasAuditLostRevenue = getAllElementAudits(v).length > 0
        hasAuditSummary ||= !!audit.hasAuditLostRevenue
      })
    }

    if (c.worksheetTrackingCodes) {
      c.worksheetTrackingCodes.forEach((v) => {
        v.audit = getElementAudit(v, workflow)
        v.audit.sourceObject = 'TrackingCode'
        v.audit.lineId = v.trackingCodeId
        setPreviousMap(v, v.audit)

        setDisagreements(v.audit, allDisagreements, workflow, v.trackingCodeId, iteration)

        audit.worksheetTrackingCodes = audit.worksheetTrackingCodes || v.audit?.changeReasons?.length
        audit.hasAuditTrackingCodes = getAllElementAudits(v).length > 0
        hasAuditSummary ||= !!audit.hasAuditTrackingCodes
      })
    }

    if (c.sendbacks) {
      c.sendbacks.forEach((v) => {
        v.audit = getElementAudit(v, workflow)
        v.audit.sourceObject = 'ReturnItem'
        v.audit.lineId = v.sendbackId
        setPreviousMap(v, v.audit)

        setDisagreements(v.audit, allDisagreements, workflow, v.sendbackId, iteration)

        audit.sendbacks = audit.sendbacks || v.audit?.changeReasons?.length
        audit.hasAuditSendbacks = getAllElementAudits(v).length > 0
        hasAuditSummary ||= !!audit.hasAuditSendbacks
      })
    }

    audit.hasAuditSummary = hasAuditSummary

    if (w.procedureMove) {
      w.procedureMoves = [w.procedureMove]
    }
    if (w.diagnosisMove) {
      w.diagnosisMoves = [w.diagnosisMove]
    }
  })
}

function hasWorkflowAudit(e: WorksheetElement, workflow: string): boolean {
  const original = e.auditMap && e.auditMap[workflow]
  const update = e.audit
  const changeReasons = update?.changeReasons || original?.changeReasons

  const hasActive = e.hasOwnProperty && e.hasOwnProperty('active')
  if (hasActive) {
    if (!e.active) {
      const previous = getPreviousMap(e)

      if (previous?.action === 'CREATE' && update?.action === 'DELETE') {
        return false
      }
      if (previous?.action === 'DELETE' && update?.action === 'CREATE') {
        return false
      }
    }
  }

  return changeReasons?.length > 0
}
function containsWorkflowAudit(e: WorksheetElement[], workflow: string): boolean {
  return !!(e || []).find((v) => hasWorkflowAudit(v, workflow))
}

export function setWorksheetAuditInfo(worksheet: Worksheet, workflow: string): Worksheet {
  const { diagnoses, procedures, downCodes, sendBacks, lostRevenues, trackingCodes } = worksheet
  const list = [
    containsWorkflowAudit(diagnoses, workflow),
    containsWorkflowAudit(procedures, workflow),
    containsWorkflowAudit(downCodes, workflow),
    containsWorkflowAudit(trackingCodes, workflow),
    containsWorkflowAudit(lostRevenues, workflow),
    containsWorkflowAudit(sendBacks, workflow),
  ]

  const hasAuditChanged = list.some((v) => v)

  worksheet.auditInfo ||= {}
  worksheet.auditInfo[workflow] ||= {}
  worksheet.auditInfo[workflow].hasAuditChanged = hasAuditChanged
  worksheet.hasAuditChanged = hasAuditChanged

  return worksheet
}
